import React, { useState } from 'react';
import { useOtelMetrics } from '../../hooks/useOtelSpans';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { DollarSign, TrendingUp, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { formatCost, PROVIDER_COLORS } from "../../lib/utils";

export const CostDashboard = ({ autoRefresh }) => {
  const [hours, setHours] = useState(24);

  const { data, isLoading, isError } = useOtelMetrics({ hours }, { autoRefresh });

  // Use shared formatCost from utils

  // Prepare data for charts
  const costByProviderData = data?.cost_by_provider ? Object.entries(data.cost_by_provider).map(([provider, cost]) => ({
    name: provider,
    value: cost,
    cost: formatCost(cost),
  })) : [];

  const totalCostData = [
    { name: 'Total Cost', value: data?.total_cost || 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Cost Analysis</h2>
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium">Time Range:</label>
          <Select
            value={hours.toString()}
            onValueChange={(value) => setHours(parseInt(value))}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last Hour</SelectItem>
              <SelectItem value="6">Last 6 Hours</SelectItem>
              <SelectItem value="24">Last 24 Hours</SelectItem>
              <SelectItem value="168">Last Week</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load cost metrics. Please check your backend connection.
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCost(data?.total_cost)}</div>
            <p className="text-xs text-muted-foreground">
              Across {data?.total_spans || 0} API calls
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Cost/Call</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCost(data?.total_spans > 0 ? data.total_cost / data.total_spans : 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Per API call
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tokens</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data?.tokens?.total_tokens?.toLocaleString() || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {data?.tokens?.total_input_tokens || 0} input, {data?.tokens?.total_output_tokens || 0} output
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pie Chart - Cost by Provider */}
        <Card>
          <CardHeader>
            <CardTitle>Cost Share by Provider</CardTitle>
            <CardDescription>Distribution of costs across LLM providers</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : costByProviderData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No cost data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={costByProviderData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {costByProviderData.map((entry) => (
                      <Cell key={entry.name} fill={PROVIDER_COLORS[entry.name]?.hex || "#71717a"} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCost(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Bar Chart - Cost by Provider */}
        <Card>
          <CardHeader>
            <CardTitle>Cost by Provider</CardTitle>
            <CardDescription>Total spend per LLM provider</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : costByProviderData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No cost data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={costByProviderData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={(value) => `$${value.toFixed(4)}`} />
                  <Tooltip formatter={(value) => formatCost(value)} />
                  <Bar dataKey="value" fill="#3b82f6">
                    {costByProviderData.map((entry) => (
                      <Cell key={entry.name} fill={PROVIDER_COLORS[entry.name]?.hex || "#71717a"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Token Usage */}
      <Card>
        <CardHeader>
          <CardTitle>Token Usage Statistics</CardTitle>
          <CardDescription>Average token consumption per API call</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-32 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Avg Input Tokens</p>
                <p className="text-2xl font-bold">{data?.tokens?.avg_input_tokens?.toFixed(0) || 0}</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Avg Output Tokens</p>
                <p className="text-2xl font-bold">{data?.tokens?.avg_output_tokens?.toFixed(0) || 0}</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Total Tokens</p>
                <p className="text-2xl font-bold">{data?.tokens?.total_tokens?.toLocaleString() || 0}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
